import { useId, useRef, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import type { DailyQuotation } from "@/features/quotation/types";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import { formatDateBR } from "@/lib/timeAgo";

type Props = {
  target: DailyQuotation | null;
  copyError: string | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title?: string;
  description?: ReactNode;
  confirmLabel?: string;
  returnFocusRef?: RefObject<HTMLElement | null>;
};

export function QuotationCopyOverwriteModal(props: Props) {
  return props.target ? (
    <OverwriteDialog {...props} target={props.target} />
  ) : null;
}

function OverwriteDialog({
  target,
  copyError,
  isPending,
  onCancel,
  onConfirm,
  title = "Cotação já existe",
  description,
  confirmLabel = "Copiar mesmo assim",
  returnFocusRef,
}: Omit<Props, "target"> & { target: DailyQuotation }) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  function cancel() {
    if (isPending) return;
    onCancel();
    window.setTimeout(() => returnFocusRef?.current?.focus(), 0);
  }

  useDialogFocus(dialogRef, cancel, { initialFocusRef: cancelRef });

  return createPortal(
    <>
      <div
        data-testid="quotation-copy-overwrite-backdrop"
        className="quotation-copy-overwrite-backdrop"
        onClick={cancel}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.38)",
          backdropFilter: "blur(6px)",
          zIndex: 1300,
        }}
      />
      <div
        ref={dialogRef}
        className="quotation-copy-overwrite-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1301,
          background: "var(--login-surface)",
          border: "1px solid var(--login-border)",
          borderRadius: "16px",
          padding: "24px",
          width: "min(92vw, 480px)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.16)",
          animation: "modalIn 0.2s ease both",
        }}
      >
        <h2
          id={titleId}
          style={{
            margin: "0 0 0.5rem",
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: "1.1875rem",
            letterSpacing: 0,
            color: "var(--login-text)",
          }}
        >
          {title}
        </h2>
        <p
          style={{
            margin: "0 0 1.25rem",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.875rem",
            color: "var(--login-muted)",
            lineHeight: 1.5,
          }}
        >
          {description ?? (
            <>
              A cotação de {formatDateBR(target.date)} já possui itens. Os itens
              da cotação de origem serão copiados, sobrescrevendo os existentes.
              Itens exclusivos da cotação de destino serão mantidos.
            </>
          )}
        </p>
        {copyError ? (
          <p style={{ margin: "0 0 0.75rem", color: "#ef4444" }} role="alert">
            {copyError}
          </p>
        ) : null}
        <div style={{ display: "flex", gap: "0.625rem" }}>
          <button
            ref={cancelRef}
            className="quotation-copy-overwrite__cancel"
            type="button"
            onClick={cancel}
            disabled={isPending}
            style={{ flex: 1, padding: "0.75rem" }}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="quotation-copy-overwrite__confirm"
            aria-label={isPending ? "Copiando..." : confirmLabel}
            onClick={onConfirm}
            disabled={isPending}
            style={{ flex: 1, padding: "0.75rem" }}
          >
            {isPending ? (
              "Copiando..."
            ) : (
              <>
                <span className="quotation-copy-overwrite__confirm-full">
                  {confirmLabel}
                </span>
                <span className="quotation-copy-overwrite__confirm-short">
                  Copiar
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
